import { UseTranslationResponse } from 'react-i18next'
import { Dispatch, SetStateAction } from 'react'

import MissingCar from '../../../assets/images/missing-car.svg'
import { DICTIONARY } from '../../../constants/dictionary'
import { GET_IMAGES_TIMEOUT_MS } from '../../../constants/images'
import { Option, Vehicle } from '../../../model/Quote'
import {
  getVehicleImageGallery,
  getVehicleImageKey,
} from '../../../utils/images/vehicleImages'
import { VehicleConfig } from '../context/vehicleConfigContext'
import { ImageUrlType, VehicleGalleryProps } from '../VehicleGallery'

export const initState = (
  props: VehicleGalleryProps,
  { state }: VehicleConfig,
  [images, setImages]: [
    ImageUrlType[],
    Dispatch<SetStateAction<ImageUrlType[]>>,
  ],
  [preloadedImages, setPreloadedImages]: [
    HTMLImageElement[],
    Dispatch<SetStateAction<HTMLImageElement[]>>,
  ],
  { t }: UseTranslationResponse<'translation', undefined>,
  [imageKey, setImageKey]: [string, Dispatch<string>],
) => {
  const exteriorColours =
    state?.vehicleConfiguration?.configurationOptions?.colours?.exteriorColours
  const vehicleTitle = `${props.vehicle.make.text} ${props.vehicle.model.text} ${props.vehicle.localVersion}`
  const electricRange =
    props.vehicle.electricRange &&
    props.vehicle.electricRange > 0 &&
    `${t(
      DICTIONARY.VEHICLE_RANGE_LABEL,
    )}: ${props.vehicle.electricRange.toString()} ${t(
      DICTIONARY.VEHICLE_RANGE_UNIT,
    )}`
  const getBodyColor = (): string => {
    const exteriorOptionSelected = exteriorColours?.find(
      (colour: Option) => colour.state === 'selected',
    )
    return exteriorOptionSelected?.optionCode || ''
  }
  const isKey = (v: Vehicle): boolean => {
    return (
      getVehicleImageKey(props.vehicle, props.locale, getBodyColor()) !==
      imageKey
    )
  }
  return {
    imagePreloadEffect: () => {
      const isImageSetUpdated =
        !images || images.length === 0 || isKey(props.vehicle)
      if (props.vehicle && exteriorColours && isImageSetUpdated) {
        setImageKey(
          getVehicleImageKey(props.vehicle, props.locale, getBodyColor()),
        )
        const bodyColour = getBodyColor()
        const imageUrls = getVehicleImageGallery(
          props.vehicle,
          props.locale,
          bodyColour,
        )
        preloadImages(
          imageUrls.map((i: ImageUrlType) => i.original),
          setPreloadedImages,
        )
        setImages(imageUrls)
      }
    },
    onPriceSectionExpandClick: () => {
      props.setPriceSectionExpanded(false)
    },
    getVehicleTitle: () => vehicleTitle,
    getElectricRange: () => electricRange,
    getImageInfo: () => t(DICTIONARY.IMAGE_INFO_MESSAGE),
    getImages: () => images,
    getPreloadedImages: () => preloadedImages,
    showElectricRange: () => !!electricRange,
    showComponent: () => images && !!preloadedImages,
    extraPadding: () => true,
  }
}

const preloadImages = (
  imageUrls: string[],
  callback: React.Dispatch<React.SetStateAction<HTMLImageElement[]>>,
) => {
  const promises: Promise<HTMLImageElement>[] = imageUrls.map((url) => {
    const loadImage = new Promise<HTMLImageElement>((resolve) => {
      const img = new Image()
      img.src = url
      img.onload = () => resolve(img)
      img.onerror = () => resolve(null as unknown as HTMLImageElement)
    })

    const timeout = new Promise<HTMLImageElement>((resolve) => {
      setTimeout(() => {
        resolve(null as unknown as HTMLImageElement)
      }, GET_IMAGES_TIMEOUT_MS)
    })

    return Promise.race([loadImage, timeout])
  })

  Promise.all(promises).then((images) => {
    const imageElements = images.filter((img) => img !== null)
    if (imageElements.length > 0) {
      callback(imageElements)
    } else {
      const defaultImg = new Image()
      defaultImg.src = MissingCar
      defaultImg.style.padding = '10%'
      callback([defaultImg])
    }
  })
}
